import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import CountryList from '../../../components/CountryList';
import { HOME_COUNTRIES_SHEET_STRICT_MODE } from '../../../config/homePageCountriesSheetBehavior';

const backdropTransition = { duration: 0.28, ease: [0.4, 0, 0.2, 1] };
const sheetTransition = { type: 'tween', ease: [0.25, 0.1, 0.25, 1], duration: 0.42 };
const sheetExpandTransition = { type: 'tween', ease: [0.25, 0.1, 0.25, 1], duration: 0.4 };

const strict = HOME_COUNTRIES_SHEET_STRICT_MODE;

/**
 * Compact (≤1024px) countries bottom sheet. Desktop is gated by the parent.
 *
 * Strict mode (see `homePageCountriesSheetBehavior.js`): no X, no backdrop/Escape dismiss,
 * no peek — full-height sheet until a country is chosen (parent then sets isOpen false).
 */
const CountriesBottomSheet = ({
  isOpen,
  onClose,
  countries,
  selectedCountry,
  onCountrySelect,
}) => {
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (strict || !isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (strict) return undefined;
    if (!isOpen) {
      setExpanded(false);
      return undefined;
    }
    const id = window.setTimeout(() => setExpanded(true), 400);
    return () => window.clearTimeout(id);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  const handleCountrySelect = useCallback(
    (country) => {
      onCountrySelect?.(country);
      onClose();
    },
    [onCountrySelect, onClose]
  );

  const sheetAnimateY = strict ? 0 : expanded ? 0 : '46%';
  const sheetTransitionUse = strict ? sheetExpandTransition : expanded ? sheetExpandTransition : sheetTransition;

  return (
    <div className="min-[1025px]:hidden">
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              key="countries-sheet-backdrop"
              role="presentation"
              className={`fixed inset-0 z-[35] bg-vervestacks-secondary/55 backdrop-blur-[1px] ${
                strict ? 'pointer-events-none' : ''
              }`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={backdropTransition}
              onClick={strict ? undefined : onClose}
            />

            <motion.div
              key="countries-sheet-panel"
              role="dialog"
              aria-modal="true"
              aria-labelledby="countries-sheet-title"
              className="vs-countries-sheet-max fixed inset-x-0 bottom-0 z-[36] flex min-h-0 flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl"
              style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
              initial={{ y: '100%' }}
              animate={{ y: sheetAnimateY }}
              exit={{ y: '100%' }}
              transition={sheetTransitionUse}
            >
              {!strict && (
                <div className="flex shrink-0 justify-center pb-1 pt-2" aria-hidden>
                  <div className="h-1.5 w-11 rounded-full bg-gray-300" />
                </div>
              )}

              <div className="flex min-h-0 min-w-0 flex-1 flex-col px-3 pb-2 pt-1">
                <CountryList
                  countries={countries}
                  selectedCountry={selectedCountry}
                  onCountrySelect={handleCountrySelect}
                  onRequestClose={strict ? null : onClose}
                  helperText="Country Name / Area ISO"
                  variant="bottomSheet"
                  titleId="countries-sheet-title"
                />
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default React.memo(CountriesBottomSheet);
